import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import Sort from "./Sort";
// Ikony używane tylko w LockSystemRow
// import { apiServer } removed

import { backendUrl } from "../backendUrl";
import LoadingPage from "../LoadingPage";
import { SystemInfo, ConfigurationDetail } from "./types";
import { getDataFromSession, storeDataInSession } from "./helpers";
import LockSystemRow from "./LockSystemRow";
import { COL_PRODUCT, COL_FEATURES, COL_PRICE, COL_EXPAND, COL_SELECT } from "./columns";

// Strzałka przy nazwie kolumny. Nieaktywna kolumna pokazuje bladą podwójną strzałkę -
// wcześniej strzałka miała display: none i nie było widać, że kolumnę można kliknąć.
const SortIndicator: React.FC<{ active: boolean; ascending: boolean }> = ({ active, ascending }) => (
  <span className={`systems-header__sort${active ? " is-active" : ""}`} aria-hidden="true">
    {!active ? <ChevronsUpDown size={15} /> : ascending ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
  </span>
);

interface Supplier {
  id: number;
  name: string;
  icon_path: string;
  display_order: number;
  is_active: number;
}

function ChooseLockSystem() {
  // Detekcja urządzeń mobilnych - działa też w narzędziach deweloperskich (F12)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const detectMobile = () => {
      const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isMobileScreen = window.innerWidth < 960; // breakpoint dla mobile
      
      // Wystarczy mała szerokość LUB mobilny user agent
      return isMobileScreen || isMobileUserAgent;
    };

    const handleResize = () => {
      setIsMobile(detectMobile());
    };
    
    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  // State for mobile filter modal
  const [showMobileFilter, setShowMobileFilter] = useState(false);

  const [doors] = useState(() => getDataFromSession("doors", []));
  const [keys] = useState(() => getDataFromSession("keys", []));
  const [pageIsLoaded, setPageIsLoaded] = useState<boolean>(false);
  const [supplierIconMap, setSupplierIconMap] = useState<Record<string, string>>({});
    const [configuration, setConfiguration] = useState<ConfigurationDetail>({
    filteredResult: [],
    maxValue: "",
    minValue: "",
  });
  const [configurationOriginal, setConfigurationOriginal] = useState<ConfigurationDetail>({
    filteredResult: [],
    maxValue: "",
    minValue: "",
  });
  const [sort, setSort] = useState("");
  const [taxRate, setTaxRate] = useState<number>(19); // default to 19% if fetch fails

  // Usunięto lokalne sortowanie, sortowanie obsługuje backend

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/v1/configurator/suppliers/active`);
        if (response && response.data) {
          const iconMap: Record<string, string> = {};
          response.data.forEach((supplier: Supplier) => {
            iconMap[supplier.name] = supplier.icon_path;
          });
          setSupplierIconMap(iconMap);
        }
      } catch (error) {
        console.error("Błąd podczas pobierania dostawców:", error);
      }
    };
    fetchSuppliers();
  }, []);

  // Opisy systemów (slogan, cechy) nie zależą od sortowania - raz przy wejściu na stronę.
  // Wcześniej były pobierane ponownie przy każdym kliknięciu w nagłówek kolumny.
  useEffect(() => {
    const fetchSystemsInfo = async () => {
      try {
        const response = await axios.get(
          `${backendUrl}/api/v1/configurator/systemsInfo`
        );
        setTableData(response && response.data ? response.data : []);
      } catch (error) {
        console.error("Błąd podczas pobierania danych:", error);
        setTableData([]);
      }
    };
    fetchSystemsInfo();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.post(
          `${backendUrl}/api/v1/configurator/configuration?sortby=${sort}`,
          [{ doors, keys }]
        );
        if (response && response.data) {
          setConfiguration(response.data);
          setConfigurationOriginal(response.data);
        } else {
          setConfiguration({ filteredResult: [], maxValue: "", minValue: "" });
          setConfigurationOriginal({ filteredResult: [], maxValue: "", minValue: "" });
        }
      } catch (error) {
        console.error("Błąd podczas pobierania danych:", error);
        setConfiguration({ filteredResult: [], maxValue: "", minValue: "" });
        setConfigurationOriginal({ filteredResult: [], maxValue: "", minValue: "" });
      } finally {
        setPageIsLoaded(true);
      }
    };
    fetchData();
  }, [doors, keys, sort]);

  // Wyniki wracają od razu; nowy klucz montuje <Sort> od nowa (czyści producenta i ceny),
  // a jego filtr i tak ustawiłby pełną listę dopiero po 100 ms
  const handleResetFilters = () => {
    setConfiguration((current) => ({
      ...current,
      filteredResult: configurationOriginal.filteredResult,
    }));
    setFilterResetKey((key) => key + 1);
  };

  const toggleRow = (index: number) => {
    const updatedRows = [...expandedRows];
    const rowIndex = updatedRows.indexOf(index);
    if (rowIndex === -1) {
      updatedRows.push(index);
    } else {
      updatedRows.splice(rowIndex, 1);
    }
    setExpandedRows(updatedRows);
  };
  // const permission = getDataFromSession("permission", []); // nieużywane
  const [tableData, setTableData] = useState<SystemInfo[]>([]);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  // Zmiana klucza montuje <Sort> od nowa - czyści filtr producenta i zakres cen
  const [filterResetKey, setFilterResetKey] = useState(0);
  const storeData = storeDataInSession;
  const handleOnSelectSystem = (supllier: String, system1: String) => {
    storeData("supllier", supllier);
    storeData("system1", system1);
  };
  // Kliknięcie tej samej kolumny odwraca kierunek, innej - zaczyna od "priceUp" / "supllierAZ".
  // Wcześniej pierwsze kliknięcie "Produkt" (bez sortowania) włączało sortowanie po cenie.
  const handleSortChange = (currentSort: string, clickedSort: string) => {
    const reversed: Record<string, string> = {
      priceUp: "priceDown",
      priceDown: "priceUp",
      supllierAZ: "supllierZA",
      supllierZA: "supllierAZ",
    };
    const sameColumn =
      (currentSort.startsWith("price") && clickedSort.startsWith("price")) ||
      (currentSort.startsWith("supllier") && clickedSort.startsWith("supllier"));
    setSort(sameColumn ? reversed[currentSort] : clickedSort);
  };

  // Fetch tax rate on component mount
  useEffect(() => {
    const fetchTaxRate = async () => {
      try {
        const response = await axios.get(
          `${backendUrl}/api/v1/configurator/tax-rate`
        );
        if (response.data && typeof response.data.rate === "number") {
          setTaxRate(response.data.rate);
        }
      } catch (err) {
        console.error("Error fetching tax rate:", err);
        // fallback to 19% on error
      }
    };
    fetchTaxRate();
  }, []);

  return (
    <div
      className="choose-lock-system"
      style={{ 
        width: isMobile ? "95%" : "75%", 
        // Na telefonie 34px od góry: tytuł zaczyna się 18px pod nagłówkiem, tak jak na stronie 3
        margin: isMobile ? "34px auto 20px" : "65px auto", 
        overflow: "hidden",
        padding: isMobile ? "0 10px" : "0"
      }}
    >
      {/* Tytuł */}
      <div style={{ textAlign: "left", margin: "20px 0", padding: "1rem" }}>
        <p
          style={{
            fontSize: "1.2rem",
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            marginBottom: "10px",
          }}
        >
          <img
            src={`${backendUrl}/img/2.png`}
            alt="Tytuł"
            style={{ width: "1.2rem", marginRight: "0.3rem" }}
          />
          Wählen Sie Ihre Schließanlage
        </p>
        
        {/* Desktop filter - show only on desktop */}
        {!isMobile && (
          <Sort
            key={filterResetKey}
            configurationOriginal={configurationOriginal}
            onConfigurationChange={setConfiguration}
            price={{
              minPrice:
                configuration.minValue && !isNaN(Number(configuration.minValue))
                  ? String(configuration.minValue)
                  : "",
              maxPrice:
                configuration.maxValue && !isNaN(Number(configuration.maxValue))
                  ? String(configuration.maxValue)
                  : "",
            }}
          />
        )}
        
        {/* Results count with mobile filter button */}
        <div className="results-header">
          <div className="results-info">
            <span className="results-count">
              {/* Przed odpowiedzią serwera było "Ergebnisse 0" - jakby nie było żadnego systemu */}
              Ergebnisse {pageIsLoaded ? configuration.filteredResult.length : "…"}
            </span>
          </div>
          
          {/* Mobile filter icon - positioned next to results */}
          {isMobile && (
            <button
              onClick={() => setShowMobileFilter(true)}
              className="mobile-filter-icon"
              title="Filter und Sortierung"
            >
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  d="M4 6H20M7 12H17M10 18H14" 
                  stroke="white" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
      
      {/* Mobile Filter Modal - zamknięty jest tylko ukryty: <Sort> trzyma ustawiony filtr,
          a odmontowany kasował go przy ponownym otwarciu (lista wracała do wszystkich systemów) */}
      {isMobile && (
        <div
          className="mobile-filter-overlay"
          style={showMobileFilter ? undefined : { display: "none" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowMobileFilter(false);
            }
          }}
        >
          <div className="mobile-filter-modal">
            {/* Close button */}
            <button
              onClick={() => setShowMobileFilter(false)}
              className="mobile-filter-close"
            >
              ×
            </button>
            
            {/* Title */}
            <h3 className="mobile-filter-title">
              Filter und Sortierung
            </h3>
            
            {/* Filter content */}
            <Sort
              key={filterResetKey}
              configurationOriginal={configurationOriginal}
              onConfigurationChange={setConfiguration}
              price={{
                minPrice:
                  configuration.minValue && !isNaN(Number(configuration.minValue))
                    ? String(configuration.minValue)
                    : "",
                maxPrice:
                  configuration.maxValue && !isNaN(Number(configuration.maxValue))
                    ? String(configuration.maxValue)
                    : "",
              }}
            />
            
            {/* Apply button */}
            <div className="mobile-filter-button-container">
              <button
                onClick={() => setShowMobileFilter(false)}
                className="mobile-filter-apply"
              >
                Anwenden
              </button>
            </div>
          </div>
        </div>
      )}
      <br />
      {/* Tabela */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          // Osobne krawędzie zamiast border + borderBottom (React ostrzegał o mieszaniu skrótu z krawędzią)
          borderTop: isMobile ? "none" : "1px solid #ddd",
          borderLeft: isMobile ? "none" : "1px solid #ddd",
          borderRight: isMobile ? "none" : "1px solid #ddd",
          borderBottom: "none",
        }}
      >
        {/* Mobile table header */}
        {isMobile && (
          <div className="mobile-table-header">
            <div 
              className="mobile-header-column product-column clickable"
              onClick={() => handleSortChange(sort, "supllierAZ")}
            >
              <span>Produkt</span>
              {sort.startsWith("supllier") && (
            <span className="mobile-sort-arrow">
              {sort === "supllierAZ" ? " ↑" : " ↓"}
            </span>
              )}
            </div>
            <div className="mobile-header-column properties-column">
              Eigenschaften
            </div>
            <div 
              className="mobile-header-column price-column clickable"
              onClick={() => handleSortChange(sort, "priceUp")}
            >
              <span>Preis inkl. MwSt. {taxRate}%</span>
              {sort.startsWith("price") && (
            <span className="mobile-sort-arrow">
              {sort === "priceUp" ? " ↑" : " ↓"}
            </span>
              )}
            </div>
          </div>
        )}

        {/* Nagłówek tabeli - tylko desktop. Kolumny z columns.ts, te same co w wierszach. */}
        {!isMobile && (
        <div className="systems-header" role="row">
          <div
            style={COL_PRODUCT}
            className="systems-header__cell systems-header__cell--sortable"
            role="columnheader"
            aria-sort={sort === "supllierAZ" ? "ascending" : sort === "supllierZA" ? "descending" : "none"}
            title="Nach Produkt sortieren"
            onClick={() => handleSortChange(sort, "supllierAZ")}
          >
            Produkt
            <SortIndicator active={sort.startsWith("supllier")} ascending={sort === "supllierAZ"} />
          </div>

          <div style={COL_FEATURES} className="systems-header__cell" role="columnheader">
            Eigenschaften
          </div>

          <div
            style={COL_PRICE}
            className="systems-header__cell systems-header__cell--sortable"
            role="columnheader"
            aria-sort={sort === "priceUp" ? "ascending" : sort === "priceDown" ? "descending" : "none"}
            title="Nach Preis sortieren"
            onClick={() => handleSortChange(sort, "priceUp")}
          >
            Preis inkl. MwSt. {taxRate}%
            <SortIndicator active={sort.startsWith("price")} ascending={sort === "priceUp"} />
          </div>

          <div style={COL_EXPAND} className="systems-header__cell" role="columnheader" />
          <div style={COL_SELECT} className="systems-header__cell" role="columnheader" />
        </div>
        )}
        {pageIsLoaded ? "" : <LoadingPage />}
        {configuration.filteredResult.map((product, index) =>
          product.price ? (
            <LockSystemRow
              key={product.supllier + '-' + product.system1}
              product={product}
              index={index}
              expanded={expandedRows.includes(index)}
              onToggle={toggleRow}
              onSelect={handleOnSelectSystem}
              configuration={configuration}
              tableData={tableData}
              supplierIconMap={supplierIconMap}
            />
          ) : null
        )}
        {pageIsLoaded && !configuration.filteredResult.some((product) => product.price) && (
          <div style={{ textAlign: "center", padding: "32px 16px" }}>
            <p style={{ fontSize: "16px", color: "#333", marginBottom: "16px" }}>
              Keine passenden Schließanlagen gefunden.
            </p>
            <div
              className="LockConfiguratorButon button-secondary"
              role="button"
              style={{ margin: "0 auto" }}
              onClick={handleResetFilters}
            >
              <div className="text-wrapper1">Filter zurücksetzen</div>
            </div>
          </div>
        )}
      </div>
      <div
        className="LockConfiguratorButons"
        style={{ width: "100%", marginTop: "3%", overflow: "hidden" }}
      >
        <Link to={"/1"}>
          <div className="LockConfiguratorButon button-secondary">
            <div className="text-wrapper1">Zurück</div>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default ChooseLockSystem;
